import { useAuth } from "@/features/auth/AuthProvider";
import { useStaff, type StaffProfile } from "@/hooks/useStaff";
import { titleCaseName } from "@/lib/names";

/**
 * Unified display profile (name, avatar, initials) for any signed-in user.
 *
 * Not everyone has a staff row — super admins and workspace owners often
 * don't. We read from both sources so the header looks right regardless:
 *
 *   name:
 *     1. staff.name (title-cased)
 *     2. user_metadata.full_name (set during invite / signup)
 *     3. humanized email local-part ("seanvparker" -> "Seanvparker")
 *     4. email address
 *
 *   avatar_url:
 *     1. staff.avatar_url
 *     2. user_metadata.avatar_url
 *     3. null (fallback to initials)
 */

export interface DisplayProfile {
  /** Title-cased display name. Never empty when a user is signed in. */
  name: string;
  /** Stable 1–2 character initials for the avatar fallback. */
  initials: string;
  /** Absolute URL to the user's avatar image, or null. */
  avatarUrl: string | null;
  /** Whether we resolved to a real staff row (determines where writes go). */
  hasStaffRecord: boolean;
  /** The underlying staff row if present — callers that need the id use this. */
  staff: StaffProfile | null;
  /** Loading reflects the staff query; the auth session is always sync here. */
  loading: boolean;
}

const USER_METADATA_NAME_KEYS = ["full_name", "name", "display_name"] as const;

export function useDisplayProfile(): DisplayProfile {
  const { user } = useAuth();
  const { data: staff, isLoading } = useStaff();

  const metadata = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const metadataName = firstString(metadata, USER_METADATA_NAME_KEYS);
  const metadataAvatar = firstString(metadata, ["avatar_url"]);

  const rawName =
    (staff?.name && staff.name.trim()) ||
    metadataName ||
    humanizeEmail(user?.email ?? "") ||
    user?.email ||
    "";

  const name = titleCaseName(rawName) || rawName || "";
  const avatarUrl = staff?.avatar_url || metadataAvatar || null;

  return {
    name,
    initials: getInitials(name, user?.email ?? "U"),
    avatarUrl,
    hasStaffRecord: Boolean(staff),
    staff: staff ?? null,
    loading: isLoading,
  };
}

function firstString(
  obj: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Turn "sean.v.parker" / "sean_parker" / "sean-parker" into "Sean V Parker".
 * Leaves ambiguous runs ("seanvparker") alone — better to show something
 * readable than to guess where the splits are.
 */
function humanizeEmail(email: string): string {
  const local = email.split("@")[0];
  if (!local) return "";
  if (/[._-]/.test(local)) {
    return local
      .split(/[._-]+/)
      .filter(Boolean)
      .join(" ");
  }
  return local;
}

function getInitials(name: string, fallback: string): string {
  const source = (name.trim() || fallback).split(/\s+/);
  return (
    source
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "U"
  );
}
